import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Crown, User, Heart, Lightbulb, Check, Loader2 } from 'lucide-react';
import api from '@/utils/api';
import { useTranslation } from 'react-i18next';

export const PricingPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);

  useEffect(() => {
    loadSubscriptionStatus();
  }, []);

  const loadSubscriptionStatus = async () => {
    try {
      const response = await api.get('/subscription/status');
      setSubscriptionStatus(response.data);
    } catch (error) {
      console.error('Error loading subscription:', error);
    }
  };

  const handleSubscribe = async (planType) => {
    setLoading(planType);

    try {
      const response = await api.post(
        '/payments/create-checkout',
        null,
        { params: { plan_type: planType } }
      );

      // Redirect to Stripe Checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Error creating checkout:', error);
      alert('Error al procesar el pago. Inténtalo de nuevo.');
      setLoading(null);
    }
  };

  const plans = [
    {
      id: 'profiles',
      icon: User,
      title: 'Perfiles Múltiples',
      price: '0,99',
      description: 'Gestiona alérgenos de toda la familia',
      features: [
        'Perfiles ilimitados',
        'Alérgenos por persona',
        'Favoritos por perfil'
      ]
    },
    {
      id: 'suggestions',
      icon: Lightbulb,
      title: 'Sugerencias Múltiples',
      price: '0,99',
      description: 'Más alternativas seguras',
      features: [
        '5 sugerencias por producto',
        'Alternativas verificadas',
        'Categorías amplias'
      ]
    },
    {
      id: 'favorites',
      icon: Heart,
      title: 'Favoritos',
      price: '0,99',
      description: 'Guarda productos seguros',
      features: [
        'Favoritos ilimitados',
        'Acceso rápido',
        'Por perfil'
      ]
    },
    {
      id: 'premium',
      icon: Crown,
      title: 'Premium Completo',
      price: '1,99',
      description: '¡Todo incluido!',
      features: [
        'Todo lo anterior',
        'Perfiles ilimitados',
        'Sugerencias ilimitadas',
        'Favoritos ilimitados',
        'Soporte prioritario'
      ],
      highlighted: true
    }
  ];

  const isSubscribed = (planId) => {
    if (!subscriptionStatus) return false;
    return subscriptionStatus.active_subscriptions.includes(planId) ||
           subscriptionStatus.is_beta_tester;
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4" style={{ color: '#8FBC8F' }}>
            Planes y Precios
          </h1>
          <p className="text-gray-600 text-lg">
            Elige el plan que mejor se adapte a tus necesidades
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Cancela cuando quieras. Sin compromiso.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.map((plan) => {
            const Icon = plan.icon;
            const subscribed = isSubscribed(plan.id);

            return (
              <Card 
                key={plan.id} 
                className={`relative ${
                  plan.highlighted 
                    ? 'border-yellow-400 border-2 shadow-xl scale-105' 
                    : 'border-gray-200'
                }`}
              >
                {plan.highlighted && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-yellow-400 text-white px-4 py-1 rounded-full text-sm font-semibold">
                    Recomendado
                  </div>
                )}

                <CardHeader>
                  <div className="flex items-center justify-center mb-4">
                    <div className={`p-3 rounded-full ${
                      plan.highlighted ? 'bg-yellow-100' : 'bg-green-100'
                    }`}>
                      <Icon className={`w-8 h-8 ${
                        plan.highlighted ? 'text-yellow-500' : 'text-[#8FBC8F]'
                      }`} />
                    </div>
                  </div>
                  <CardTitle className="text-center">{plan.title}</CardTitle>
                  <CardDescription className="text-center">
                    {plan.description}
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-4">
                  <div className="text-center py-4">
                    <p className="text-4xl font-bold" style={{ color: '#8FBC8F' }}>
                      €{plan.price}
                    </p>
                    <p className="text-sm text-gray-500">por mes</p>
                  </div>

                  <div className="space-y-2 min-h-[120px]">
                    {plan.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-2 text-sm">
                        <Check className="w-4 h-4 text-[#8FBC8F] mt-0.5 flex-shrink-0" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>

                  <Button
                    className={`w-full ${
                      plan.highlighted
                        ? 'bg-yellow-500 hover:bg-yellow-600'
                        : 'bg-[#8FBC8F] hover:bg-[#7aad7a]'
                    }`}
                    onClick={() => handleSubscribe(plan.id)}
                    disabled={loading === plan.id || subscribed}
                  >
                    {loading === plan.id ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Procesando...</>
                    ) : subscribed ? (
                      <>Activo</>
                    ) : (
                      <>Suscribirse</>
                    )}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="mt-12 text-center">
          <Button variant="outline" onClick={() => navigate('/')}>
            Volver al inicio
          </Button>
        </div>
      </div>
    </div>
  );
};